import {
  Container,
  Grid,
  LinearProgress,
  Snackbar,
  Typography,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RedirectKeepUTM } from "../../../shared/components/RedirectKeepUTM";

import { Header } from "../../../shared/components/header";
import { ContentTable } from "../../components";

import { useFilterPagination } from "../../../shared/hooks/useFilterPagination";
import {
  resetRequest,
  setClose,
} from "../../../shared/request/actions/requestActions";
import {
  asyncFetchAllMyAssignedContent,
  asyncGetResourcesType,
} from "../../actions/contentActions";
import { getAllAvailableContent } from "../../service";
import { useStyles } from "./styles";

const DEFAULT_SEARCH = {
  title: "",
  isArticle: true,
  isResource: true,
  isMoneyHack: true,
};

export const ListAllContent = ({ navigation }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [search, setSearch] = useState({
    ...DEFAULT_SEARCH,
    contentType: "All",
  });

  const { request, organizationSettings } = useSelector((state) => ({
    request: state.request.request,
    organizationSettings: state.auth.login.organization.settings,
  }));

  useEffect(() => {
    dispatch(resetRequest());
    dispatch(asyncFetchAllMyAssignedContent());
    dispatch(asyncGetResourcesType());
  }, [dispatch]);

  const {
    order,
    orderBy,
    infiniteRef,
    isFetching,
    sourceData,
    totalDataRecords,
    removeItemFromData,
    debouncedHandleSortChange,
    debouncedHandleFilterChange,
    debouncedHandleFiltersChange,
  } = useFilterPagination(
    getAllAvailableContent,
    10,
    "contentId",
    {
      ...DEFAULT_SEARCH,
    },
    ""
  );

  const handleRequestSort = useCallback(
    (property) => {
      const isAsc = orderBy === property && order === "asc";
      debouncedHandleSortChange(property, isAsc ? "desc" : "asc");
    },
    [orderBy, order, debouncedHandleSortChange]
  );

  const handleFilterTableChange = (event) => {
    const { value, name } = event.target;
    setSearch({ ...search, [name]: value });
    debouncedHandleFilterChange(value, name);
  };

  const handleContentTypeChange = (event) => {
    const { value, name } = event.target;
    const dataToUpdate = { [name]: value };

    const paginationSearch = {
      isArticle: false,
      isResource: false,
      isMoneyHack: false,
    };

    setSearch({ ...search, ...dataToUpdate });
    if (value === "All") {
      paginationSearch.isArticle = true;
      paginationSearch.isResource = true;
      paginationSearch.isMoneyHack = true;
    } else {
      paginationSearch[value] = true;
    }

    setSearch({ ...search, ...dataToUpdate, ...paginationSearch });
    debouncedHandleFiltersChange(paginationSearch);
  };

  const filters = [
    {
      name: "keyword",
      id: "keyword",
      type: "text",
      label: "Keyword",
      value: search.title,
      onChange: handleFilterTableChange,
    },
    {
      name: "contentType",
      id: "contentType",
      type: "dropDown",
      label: "Content Type",
      options: [
        { text: "All", id: "All" },
        { text: "Article", id: "isArticle" },
        { text: "Resource", id: "isResource" },
        { text: "Money Hack", id: "isMoneyHack" },
      ],
      value: search.contentType,
      onChange: handleContentTypeChange,
    },
  ];

  const handleAlertClose = () => {
    dispatch(setClose());
  };

  if (organizationSettings.isContentFunctionalityEnabled === false) {
    return <RedirectKeepUTM from="/content" to="/participant" />;
  }

  return (
    <>
      <Header navigation={navigation}>
        <Typography variant="h2" color="textPrimary" component="h1">
          All available content
        </Typography>
        <Container component="main">
          {request.isFetching && (
            <div className={classes.root}>
              <LinearProgress color="secondary" />
            </div>
          )}
          <Grid container className={classes.marginSpacing}>
            <Grid item xs={12}>
              <Typography>{`Total Records: ${totalDataRecords}`}</Typography>
            </Grid>
            {!sourceData.length && (
              <Grid item xs={12}>
                <Typography align="center" variant="h6">
                  There are no content defined
                </Typography>
              </Grid>
            )}
          </Grid>
          <Grid spacing={5} container ref={infiniteRef}>
            <ContentTable
              content={sourceData}
              removeTodo={removeItemFromData}
              order={order}
              orderBy={orderBy}
              onSortChange={handleRequestSort}
              filters={filters}
            />
            {isFetching && <Typography>Loading...</Typography>}
          </Grid>
        </Container>
      </Header>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={request.isOpen}
        autoHideDuration={6000}
        onClose={handleAlertClose}
      >
        <Alert onClose={handleAlertClose} severity={request.type}>
          {request.message}
        </Alert>
      </Snackbar>
    </>
  );
};

ListAllContent.propTypes = {
  navigation: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      path: PropTypes.string.isRequired,
    })
  ),
};

ListAllContent.defaultProps = {
  navigation: [],
};
